import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, Truck, Package, CheckCircle,
  Clock, CreditCard, User, Weight, DollarSign,
  FileText, Download, Eye, ChevronDown, ChevronUp, AlertCircle, XCircle, Calendar, Building2, Receipt, History, X, Image, Shield, HelpCircle, Scan
} from 'lucide-react';

const DELIVERY_STATUS_CONFIG = {
  '待送货': { color: 'bg-slate-500', icon: Clock, textColor: 'text-slate-600', lightBg: 'bg-slate-100' },
  '已到场': { color: 'bg-blue-500', icon: Truck, textColor: 'text-blue-600', lightBg: 'bg-blue-100' },
  '已入库': { color: 'bg-purple-500', icon: Package, textColor: 'text-purple-600', lightBg: 'bg-purple-100' },
  '待结算': { color: 'bg-orange-500', icon: AlertCircle, textColor: 'text-orange-600', lightBg: 'bg-orange-100' },
  '已结算': { color: 'bg-amber-500', icon: DollarSign, textColor: 'text-amber-600', lightBg: 'bg-amber-100' },
  '已开票': { color: 'bg-teal-500', icon: Receipt, textColor: 'text-teal-600', lightBg: 'bg-teal-100' },
  '已付款': { color: 'bg-green-500', icon: CreditCard, textColor: 'text-green-600', lightBg: 'bg-green-100' },
};

const STATUS_ORDER = ['待送货', '已到场', '已入库', '待结算', '已结算', '已开票', '已付款'];
const IN_PROGRESS_STATUSES = ['已到场', '已入库'];
const NEED_CONFIRM_STATUS = '待结算';

const DocumentPreviewModal = ({ visible, onClose, document }) => {
  if (!visible || !document) return null;
  
  const { documentType, documentData, document: docName } = document;
  
  const renderArrivalNotice = () => (
    <div className="space-y-4">
      <div className="text-center pb-4 border-b border-slate-200">
        <h3 className="text-lg font-bold text-slate-800">预到货通知单</h3>
        <p className="text-xs text-slate-500 mt-1">{docName}</p>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">车牌号</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.vehicleNo || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">送货日期</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.deliveryDate || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">司机姓名</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.driverName || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">联系方式</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.driverPhone || '-'}</span>
        </div>
      </div>
    </div>
  );
  
  const renderReceipt = () => (
    <div className="space-y-4">
      <div className="text-center pb-4 border-b border-slate-200">
        <h3 className="text-lg font-bold text-slate-800">收货单</h3>
        <p className="text-xs text-slate-500 mt-1">{docName}</p>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">车牌号</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.vehicleNo || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">到场时间</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.arrivalTime || '-'}</span>
        </div>
        <div className="py-2">
          <span className="text-sm text-slate-500 block mb-2">到场照片</span>
          {documentData?.arrivalPhoto ? (
            <img src={documentData.arrivalPhoto} alt="到场照片" className="w-full rounded-lg border border-slate-200" />
          ) : (
            <div className="w-full h-32 bg-slate-100 rounded-lg flex items-center justify-center">
              <Image size={32} className="text-slate-300" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  const renderWarehouse = () => (
    <div className="space-y-4">
      <div className="text-center pb-4 border-b border-slate-200">
        <h3 className="text-lg font-bold text-slate-800">入库单</h3>
        <p className="text-xs text-slate-500 mt-1">{docName}</p>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">物料名称</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.materialName || '-'}</span>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-slate-50 rounded-lg p-3">
            <span className="text-xs text-slate-500 block">皮重</span>
            <span className="text-sm font-bold text-slate-800">{documentData?.tareWeight || '-'} 吨</span>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <span className="text-xs text-slate-500 block">毛重</span>
            <span className="text-sm font-bold text-slate-800">{documentData?.grossWeight || '-'} 吨</span>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <span className="text-xs text-blue-500 block">净重</span>
            <span className="text-sm font-bold text-blue-700">{documentData?.netWeight || '-'} 吨</span>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <span className="text-xs text-slate-500 block">预估干基</span>
            <span className="text-sm font-bold text-slate-800">{documentData?.estimatedDryBasis || '-'} 吨</span>
          </div>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">扣杂</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.deduction || '-'} 吨</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">单价</span>
          <span className="text-sm font-medium text-slate-800">¥{documentData?.unitPrice || '-'}/吨</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">总价</span>
          <span className="text-sm font-bold text-red-600">¥{documentData?.totalAmount?.toLocaleString() || '-'}</span>
        </div>
        <div className="py-2">
          <span className="text-sm text-slate-500 block mb-2">现场图片</span>
          <div className="grid grid-cols-2 gap-2">
            {documentData?.photos?.map((photo, i) => (
              <img key={i} src={photo} alt={`现场图片${i + 1}`} className="w-full rounded-lg border border-slate-200" />
            )) || (
              <div className="col-span-2 w-full h-24 bg-slate-100 rounded-lg flex items-center justify-center">
                <Image size={32} className="text-slate-300" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  
  const renderSettlement = () => (
    <div className="space-y-4">
      <div className="text-center pb-4 border-b border-slate-200">
        <h3 className="text-lg font-bold text-slate-800">结算单</h3>
        <p className="text-xs text-slate-500 mt-1">{docName}</p>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">结算日期</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.settlementDate || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">结算重量</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.settlementWeight || '-'} 吨</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">含税金额</span>
          <span className="text-sm font-bold text-red-600">¥{documentData?.taxIncludedAmount?.toLocaleString() || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">未税金额</span>
          <span className="text-sm font-medium text-slate-800">¥{documentData?.taxExcludedAmount?.toLocaleString() || '-'}</span>
        </div>
      </div>
    </div>
  );
  
  const renderInvoice = () => (
    <div className="space-y-4">
      <div className="text-center pb-4 border-b border-slate-200">
        <h3 className="text-lg font-bold text-slate-800">电子发票</h3>
        <p className="text-xs text-slate-500 mt-1">{docName}</p>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">销方名称</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.sellerName || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">发票号</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.invoiceNo || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">发票金额</span>
          <span className="text-sm font-bold text-red-600">¥{documentData?.invoiceAmount?.toLocaleString() || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">开票日期</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.invoiceDate || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">税率</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.taxRate || '-'}</span>
        </div>
        <div className="py-2">
          <span className="text-sm text-slate-500 block mb-2">电子发票</span>
          {documentData?.invoiceFile ? (
            <img src={documentData.invoiceFile} alt="电子发票" className="w-full rounded-lg border border-slate-200" />
          ) : (
            <div className="w-full h-48 bg-slate-100 rounded-lg flex items-center justify-center">
              <FileText size={32} className="text-slate-300" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
  
  const renderPayment = () => (
    <div className="space-y-4">
      <div className="text-center pb-4 border-b border-slate-200">
        <h3 className="text-lg font-bold text-slate-800">付款凭证</h3>
        <p className="text-xs text-slate-500 mt-1">{docName}</p>
      </div>
      <div className="space-y-3">
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">收款名称</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.receiverName || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">收款账号</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.receiverAccount || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">付款时间</span>
          <span className="text-sm font-medium text-slate-800">{documentData?.paymentTime || '-'}</span>
        </div>
        <div className="flex justify-between items-center py-2 border-b border-slate-100">
          <span className="text-sm text-slate-500">付款金额</span>
          <span className="text-sm font-bold text-red-600">¥{documentData?.paymentAmount?.toLocaleString() || '-'}</span>
        </div>
      </div>
    </div>
  );
  
  const renderContent = () => {
    switch (documentType) {
      case 'arrivalNotice':
        return renderArrivalNotice();
      case 'receipt':
        return renderReceipt();
      case 'warehouse':
        return renderWarehouse();
      case 'settlement':
        return renderSettlement();
      case 'invoice':
        return renderInvoice();
      case 'payment':
        return renderPayment();
      default:
        return (
          <div className="text-center py-8 text-slate-500">
            暂无单据详情
          </div>
        );
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" onClick={onClose}>
      <div 
        className="bg-white w-full max-w-md rounded-t-2xl max-h-[85vh] overflow-hidden animate-slide-up"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between z-10">
          <h2 className="text-base font-bold text-slate-800">单据详情</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full">
            <X size={20} className="text-slate-500" />
          </button>
        </div>
        <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 56px)' }}>
          {renderContent()}
        </div>
        <div className="sticky bottom-0 bg-white border-t border-slate-200 p-4">
          <button 
            onClick={onClose}
            className="w-full py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
          >
            <Download size={18} />
            下载单据
          </button>
        </div>
      </div>
    </div>
  );
};

const HISTORY_STORAGE_KEY = 'delivery_history_records';
const MAX_HISTORY_RECORDS = 10;

const getHistoryRecords = () => {
  try {
    const records = localStorage.getItem(HISTORY_STORAGE_KEY);
    return records ? JSON.parse(records) : [];
  } catch {
    return [];
  }
};

const saveHistoryRecord = (record) => {
  if (!record.vehicleNo) return;
  
  const records = getHistoryRecords();
  const existingIndex = records.findIndex(r => r.vehicleNo === record.vehicleNo);
  
  if (existingIndex >= 0) {
    records[existingIndex] = { ...record, updatedAt: new Date().toISOString() };
  } else {
    records.unshift({ ...record, createdAt: new Date().toISOString() });
  }
  
  const trimmedRecords = records.slice(0, MAX_HISTORY_RECORDS);
  localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(trimmedRecords));
};

const ScheduleDeliveryModal = ({ visible, onClose, onSubmit, order }) => {
  const [formData, setFormData] = useState({
    vehicleNo: '',
    deliveryDate: '',
    driverName: '',
    driverPhone: '',
  });
  const [historyRecords, setHistoryRecords] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  
  useEffect(() => {
    if (visible) {
      setHistoryRecords(getHistoryRecords());
    }
  }, [visible]);
  
  const handleSubmit = () => {
    if (!formData.vehicleNo || !formData.deliveryDate) {
      alert('请填写车牌号和送货日期');
      return;
    }
    saveHistoryRecord({
      vehicleNo: formData.vehicleNo,
      driverName: formData.driverName,
      driverPhone: formData.driverPhone,
    });
    onSubmit(formData);
    setFormData({ vehicleNo: '', deliveryDate: '', driverName: '', driverPhone: '' });
    setShowHistory(false);
  };
  
  const handleSelectHistory = (record) => {
    setFormData({
      ...formData,
      vehicleNo: record.vehicleNo || '',
      driverName: record.driverName || '',
      driverPhone: record.driverPhone || '',
    });
    setShowHistory(false);
    setFocusedField(null);
  };
  
  const handleClearHistory = () => {
    localStorage.removeItem(HISTORY_STORAGE_KEY);
    setHistoryRecords([]);
    setShowHistory(false);
  };
  
  const filteredHistory = historyRecords.filter(record => {
    if (!focusedField) return true;
    if (focusedField === 'vehicleNo') return true;
    return true;
  });
  
  if (!visible) return null;
  
  const today = new Date().toISOString().split('T')[0];
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
      <div className="bg-white rounded-t-3xl w-full max-w-md shadow-2xl animate-slide-up">
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-800">预约发货</h3>
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-full">
              <XCircle size={24} className="text-slate-400" />
            </button>
          </div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-slate-500">订单号：{order?.orderNo}</span>
            <span className="text-xs text-slate-300">|</span>
            <span className="text-xs text-blue-600 font-medium">{order?.material}</span>
          </div>
        </div>
        
        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          {historyRecords.length > 0 && (
            <div className="mb-2">
              <button
                type="button"
                onClick={() => setShowHistory(!showHistory)}
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 transition-colors"
              >
                <History size={14} />
                <span>历史记录 ({historyRecords.length}条)</span>
                <ChevronDown size={14} className={`transition-transform ${showHistory ? 'rotate-180' : ''}`} />
              </button>
              
              {showHistory && (
                <div className="mt-2 space-y-2">
                  {filteredHistory.map((record, index) => (
                    <div
                      key={index}
                      onClick={() => handleSelectHistory(record)}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 cursor-pointer hover:bg-blue-50 hover:border-blue-200 transition-all"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Truck size={14} className="text-blue-600" />
                        </div>
                        <div>
                          <div className="text-sm font-bold text-slate-800">{record.vehicleNo}</div>
                          {(record.driverName || record.driverPhone) && (
                            <div className="text-xs text-slate-500">
                              {record.driverName && <span>{record.driverName}</span>}
                              {record.driverName && record.driverPhone && <span> · </span>}
                              {record.driverPhone && <span>{record.driverPhone}</span>}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-xs text-blue-500">使用</div>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={handleClearHistory}
                    className="w-full text-xs text-slate-400 hover:text-red-500 transition-colors py-2"
                  >
                    清空历史记录
                  </button>
                </div>
              )}
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              车牌号 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.vehicleNo}
              onChange={(e) => setFormData({ ...formData, vehicleNo: e.target.value })}
              onFocus={() => setFocusedField('vehicleNo')}
              onBlur={() => setTimeout(() => setFocusedField(null), 200)}
              placeholder="请输入车牌号，如：浙A12345"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              送货日期 <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={formData.deliveryDate}
              onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
              min={today}
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              司机姓名 <span className="text-slate-400 text-xs font-normal">(选填)</span>
            </label>
            <input
              type="text"
              value={formData.driverName}
              onChange={(e) => setFormData({ ...formData, driverName: e.target.value })}
              onFocus={() => setFocusedField('driverName')}
              onBlur={() => setTimeout(() => setFocusedField(null), 200)}
              placeholder="请输入司机姓名"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              联系电话 <span className="text-slate-400 text-xs font-normal">(选填)</span>
            </label>
            <input
              type="tel"
              value={formData.driverPhone}
              onChange={(e) => setFormData({ ...formData, driverPhone: e.target.value })}
              onFocus={() => setFocusedField('driverPhone')}
              onBlur={() => setTimeout(() => setFocusedField(null), 200)}
              placeholder="请输入司机联系电话"
              className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>
        </div>
        
        <div className="p-4 border-t border-slate-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-bold hover:bg-blue-600 transition-colors shadow-sm"
          >
            确认预约
          </button>
        </div>
      </div>
    </div>
  );
};

const SettlementConfirmSection = ({ delivery, onConfirm, onReject }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [invoicePolicyAgreed, setInvoicePolicyAgreed] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [showFaceRecognition, setShowFaceRecognition] = useState(false);
  const [faceRecognitionSuccess, setFaceRecognitionSuccess] = useState(false);
  const [step, setStep] = useState(1);
  
  const needFaceRecognition = delivery.amount >= 10000;
  const totalSteps = needFaceRecognition ? 3 : 2;
  
  const handleConfirmClick = () => {
    if (!invoicePolicyAgreed) {
      setShowPolicyModal(true);
      return;
    }
    
    if (needFaceRecognition && !faceRecognitionSuccess) {
      setStep(2);
      return;
    }
    
    onConfirm(delivery.id);
    handleCloseModal();
  };
  
  const handleFaceRecognitionComplete = (success) => {
    setFaceRecognitionSuccess(success);
    setShowFaceRecognition(false);
    if (success) {
      setStep(3);
    }
  };
  
  const handlePolicyAgree = () => {
    setInvoicePolicyAgreed(true);
    setShowPolicyModal(false);
    if (needFaceRecognition) {
      setStep(2);
    } else {
      setStep(2);
    }
  };
  
  const handleCloseModal = () => {
    setShowConfirmModal(false);
    setStep(1);
    setInvoicePolicyAgreed(false);
    setFaceRecognitionSuccess(false);
  };
  
  return (
    <>
      <div className="pt-4 -mx-4 px-4 pb-3 bg-gradient-to-b from-orange-50 to-transparent border-b-2 border-orange-200">
        <div className="text-xs font-bold text-orange-600 mb-2 flex items-center gap-1">
          <AlertCircle size={12} />
          结算确认
        </div>
        
        <div className="bg-white rounded-lg p-3 border border-orange-200 shadow-sm">
          <div className="flex items-center justify-between py-2 border-b border-slate-100 mb-3">
            <div className="text-center flex-1">
              <div className="text-xs text-slate-500">入库重量</div>
              <div className="text-base font-bold text-slate-800">{delivery.weight}吨</div>
            </div>
            <div className="text-slate-300 text-lg">×</div>
            <div className="text-center flex-1">
              <div className="text-xs text-slate-500">结算单价</div>
              <div className="text-base font-bold text-slate-800">¥{delivery.unitPrice}/吨</div>
            </div>
            <div className="text-slate-300 text-lg">=</div>
            <div className="text-center flex-1">
              <div className="text-xs text-slate-500">结算金额</div>
              <div className="text-xl font-bold text-red-600">¥{delivery.amount.toLocaleString()}</div>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => setShowRejectModal(true)}
              className="flex-1 py-2.5 rounded-lg border border-red-200 text-red-600 text-sm font-bold hover:bg-red-50 transition-colors flex items-center justify-center gap-1"
            >
              <XCircle size={16} />
              驳回
            </button>
            <button 
              onClick={() => {
                setShowConfirmModal(true);
                setStep(1);
              }}
              className="flex-1 py-2.5 rounded-lg bg-green-500 text-white text-sm font-bold hover:bg-green-600 transition-colors flex items-center justify-center gap-1 shadow-sm"
            >
              <CheckCircle size={16} />
              确认结算
            </button>
          </div>
        </div>
      </div>
      
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-end justify-center">
          <div className="bg-white rounded-t-3xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-hidden flex flex-col animate-slide-up">
            <div className="sticky top-0 bg-white z-10 border-b border-slate-100">
              <div className="flex items-center justify-between px-5 py-4">
                <button 
                  onClick={handleCloseModal}
                  className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-colors"
                >
                  <X size={18} />
                </button>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-slate-800">确认结算</span>
                  <span className="text-xs text-slate-400">({step}/{totalSteps})</span>
                </div>
                <div className="w-8" />
              </div>
              
              <div className="px-5 pb-3">
                <div className="flex items-center gap-2">
                  {[...Array(totalSteps)].map((_, i) => (
                    <div 
                      key={i}
                      className={`flex-1 h-1 rounded-full transition-all ${
                        i + 1 <= step ? 'bg-green-500' : 'bg-slate-200'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {step === 1 && (
                <div className="p-5 space-y-5">
                  <div className="text-center py-4">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-400 to-green-600 mb-4 shadow-lg">
                      <DollarSign size={36} className="text-white" />
                    </div>
                    <div className="text-sm text-slate-500 mb-2">结算金额</div>
                    <div className="text-4xl font-bold text-slate-800">
                      ¥<span className="text-red-600">{delivery.amount.toLocaleString()}</span>
                    </div>
                    <div className="text-sm text-slate-400 mt-2">
                      {delivery.weight}吨 × ¥{delivery.unitPrice}/吨
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100/50 rounded-2xl p-4 border border-purple-200/50">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center shrink-0">
                        <FileText size={16} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-bold text-slate-800 text-sm">反向开票政策</span>
                          <button
                            type="button"
                            onClick={() => setShowPolicyModal(true)}
                            className="text-purple-500 hover:text-purple-600"
                          >
                            <HelpCircle size={14} />
                          </button>
                        </div>
                        <p className="text-xs text-slate-600 leading-relaxed">
                          根据《国家税务总局公告2024年第5号》，平台将为您代办"反向开票"及相关税费申报。
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div 
                    className={`rounded-2xl p-4 border-2 transition-all cursor-pointer ${
                      invoicePolicyAgreed 
                        ? 'bg-green-50 border-green-300' 
                        : 'bg-white border-slate-200 hover:border-purple-300'
                    }`}
                    onClick={() => {
                      if (!invoicePolicyAgreed) {
                        setShowPolicyModal(true);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        invoicePolicyAgreed 
                          ? 'bg-green-500 border-green-500' 
                          : 'border-slate-300'
                      }`}>
                        {invoicePolicyAgreed && <CheckCircle size={14} className="text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="text-sm text-slate-700 leading-relaxed">
                          我已阅读并同意
                          <span 
                            className="font-medium text-purple-500 cursor-pointer" 
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowPolicyModal(true);
                            }}
                          >
                            《反向开票政策告知书》
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-amber-50/80 rounded-xl p-3 flex items-start gap-2">
                    <AlertCircle size={14} className="text-amber-500 shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-700 leading-relaxed">
                      <span className="font-bold">提示：</span>
                      连续12个月累计销售额不得超过500万元
                    </div>
                  </div>
                </div>
              )}
              
              {step === 2 && needFaceRecognition && (
                <div className="p-5 space-y-5">
                  <div className="text-center py-6">
                    <div className={`w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center transition-all ${
                      faceRecognitionSuccess 
                        ? 'bg-green-100' 
                        : 'bg-blue-100'
                    }`}>
                      {faceRecognitionSuccess ? (
                        <CheckCircle size={48} className="text-green-500" />
                      ) : (
                        <Scan size={48} className="text-blue-500" />
                      )}
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">
                      {faceRecognitionSuccess ? '验证成功' : '人脸识别验证'}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {faceRecognitionSuccess 
                        ? '您已通过身份验证，可以确认结算' 
                        : '结算金额≥1万元，需进行人脸识别验证'
                      }
                    </p>
                  </div>
                  
                  {!faceRecognitionSuccess && (
                    <div className="space-y-4">
                      <div className="bg-slate-50 rounded-xl p-4">
                        <div className="text-sm font-medium text-slate-700 mb-3">验证步骤</div>
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold">1</div>
                            <span className="text-sm text-slate-600">确保光线充足，面部清晰可见</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold">2</div>
                            <span className="text-sm text-slate-600">将面部对准识别框内</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center font-bold">3</div>
                            <span className="text-sm text-slate-600">保持静止，等待验证完成</span>
                          </div>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => setShowFaceRecognition(true)}
                        className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
                      >
                        <Scan size={20} />
                        开始人脸识别
                      </button>
                    </div>
                  )}
                  
                  {faceRecognitionSuccess && (
                    <button
                      onClick={() => {
                        setStep(3);
                      }}
                      className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
                    >
                      下一步
                    </button>
                  )}
                </div>
              )}
              
              {step === 2 && !needFaceRecognition && (
                <div className="p-5 space-y-5">
                  <div className="text-center py-8">
                    <div className="w-20 h-20 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
                      <CheckCircle size={40} className="text-green-500" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">确认结算</h3>
                    <p className="text-sm text-slate-500">请确认结算信息无误后提交</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">结算金额</span>
                      <span className="text-lg font-bold text-red-600">¥{delivery.amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">结算重量</span>
                      <span className="text-sm font-medium text-slate-800">{delivery.weight}吨</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">结算单价</span>
                      <span className="text-sm font-medium text-slate-800">¥{delivery.unitPrice}/吨</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      onConfirm(delivery.id);
                      handleCloseModal();
                    }}
                    className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
                  >
                    确认结算
                  </button>
                </div>
              )}
              
              {step === 3 && needFaceRecognition && (
                <div className="p-5 space-y-5">
                  <div className="text-center py-8">
                    <div className="w-20 h-20 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
                      <CheckCircle size={40} className="text-green-500" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">确认结算</h3>
                    <p className="text-sm text-slate-500">请确认结算信息无误后提交</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">结算金额</span>
                      <span className="text-lg font-bold text-red-600">¥{delivery.amount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">结算重量</span>
                      <span className="text-sm font-medium text-slate-800">{delivery.weight}吨</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-500">结算单价</span>
                      <span className="text-sm font-medium text-slate-800">¥{delivery.unitPrice}/吨</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                      <span className="text-sm text-slate-500">身份验证</span>
                      <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                        <CheckCircle size={14} />
                        已通过
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => {
                      onConfirm(delivery.id);
                      handleCloseModal();
                    }}
                    className="w-full py-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
                  >
                    确认结算
                  </button>
                </div>
              )}
            </div>
            
            {step === 1 && (
              <div className="sticky bottom-0 bg-white px-5 py-4 border-t border-slate-100">
                <button 
                  onClick={handleConfirmClick}
                  disabled={!invoicePolicyAgreed}
                  className={`w-full py-4 rounded-xl font-bold text-base transition-all ${
                    invoicePolicyAgreed 
                      ? 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg hover:shadow-xl active:scale-[0.98]' 
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  {invoicePolicyAgreed ? '下一步' : '请先同意政策告知书'}
                </button>
              </div>
            )}
            
            {step === 2 && needFaceRecognition && !faceRecognitionSuccess && (
              <div className="sticky bottom-0 bg-white px-5 py-4 border-t border-slate-100">
                <button 
                  onClick={() => setStep(1)}
                  className="w-full py-3 border border-slate-200 rounded-xl text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                >
                  返回上一步
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {showPolicyModal && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-end justify-center">
          <div className="bg-white rounded-t-3xl w-full max-w-md max-h-[90vh] overflow-hidden shadow-2xl animate-slide-up">
            <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-100 z-10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                    <FileText size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <div className="font-bold text-slate-800">反向开票政策告知书</div>
                    <div className="text-xs text-slate-500">国家税务总局公告2024年第5号</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowPolicyModal(false)}
                  className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 hover:bg-slate-200 transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto p-5 space-y-4" style={{ maxHeight: 'calc(90vh - 160px)' }}>
              <div className="bg-purple-50 rounded-xl p-4 border border-purple-100">
                <div className="flex items-center gap-2 mb-2">
                  <Shield size={16} className="text-purple-600" />
                  <span className="font-bold text-purple-800">什么是反向开票？</span>
                </div>
                <p className="text-sm text-purple-700 leading-relaxed">
                  根据《国家税务总局关于资源回收企业向自然人报废产品出售者"反向开票"有关事项的公告》（2024年第5号），资源回收企业可以向自然人出售者开具发票，即由购买方向销售方开具发票，称为"反向开票"。
                </p>
              </div>

              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center gap-2 mb-2">
                  <User size={16} className="text-blue-600" />
                  <span className="font-bold text-blue-800">适用对象</span>
                </div>
                <ul className="text-sm text-blue-700 leading-relaxed space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>销售自己使用过的报废产品的自然人</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-500 mt-1">•</span>
                    <span>销售收购的报废产品的自然人</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle size={16} className="text-amber-600" />
                  <span className="font-bold text-amber-800">重要限制条件</span>
                </div>
                <div className="text-sm text-amber-700 leading-relaxed space-y-3">
                  <div className="bg-white rounded-lg p-3 border border-amber-100">
                    <div className="font-bold text-amber-800 mb-1">销售额限制</div>
                    <p>自然人连续不超过12个月"反向开票"累计销售额（不含增值税）不得超过<strong>500万元</strong>。超过限额后，资源回收企业不得再向其"反向开票"，需引导其依法办理经营主体登记，自行开具发票。</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-amber-100">
                    <div className="font-bold text-amber-800 mb-1">月销售额提示</div>
                    <p>当月销售额超过<strong>10万元</strong>时，税务机关将发出提示，需按规定代办增值税及附加税费申报。</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 rounded-xl p-4 border border-green-100">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle size={16} className="text-green-600" />
                  <span className="font-bold text-green-800">税费优惠政策</span>
                </div>
                <ul className="text-sm text-green-700 leading-relaxed space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span>月销售额10万元以下：免征增值税</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span>月销售额超过10万元：3%征收率减按1%计算缴纳增值税</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-500 mt-1">•</span>
                    <span>个人所得税：次年3月31日前办理经营所得汇算清缴</span>
                  </li>
                </ul>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <FileText size={16} className="text-slate-600" />
                  <span className="font-bold text-slate-800">代办税费说明</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">
                  资源回收企业向出售者"反向开票"时，应当按规定为出售者代办以下税费申报事项：
                </p>
                <ul className="text-sm text-slate-600 leading-relaxed mt-2 space-y-1">
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 mt-1">•</span>
                    <span>增值税及附加税费</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-slate-400 mt-1">•</span>
                    <span>个人所得税</span>
                  </li>
                </ul>
              </div>

              <div className="bg-red-50 rounded-xl p-4 border border-red-100">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle size={16} className="text-red-600" />
                  <span className="font-bold text-red-800">法律责任提示</span>
                </div>
                <p className="text-sm text-red-700 leading-relaxed">
                  出售者应确保业务真实性，如实提供身份信息。如存在虚假交易、虚开发票等违法行为，将承担相应的法律责任，包括追缴税款、加收滞纳金、处以罚款等。
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white px-5 py-4 border-t border-slate-100">
              <button
                onClick={handlePolicyAgree}
                className="w-full py-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
              >
                我已阅读并同意
              </button>
            </div>
          </div>
        </div>
      )}
      
      {showFaceRecognition && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="relative">
              <div className="absolute top-4 right-4 z-10">
                <button 
                  onClick={() => setShowFaceRecognition(false)}
                  className="w-10 h-10 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="bg-gradient-to-b from-blue-500 to-blue-600 pt-12 pb-8 px-6 text-center">
                <div className="w-20 h-20 rounded-full bg-white/20 mx-auto mb-4 flex items-center justify-center">
                  <Scan size={40} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-1">人脸识别验证</h3>
                <p className="text-sm text-white/80">请将面部对准框内，保持光线充足</p>
              </div>
              
              <div className="p-6">
                <div className="relative w-48 h-48 mx-auto mb-6">
                  <div className="absolute inset-0 rounded-full border-4 border-dashed border-blue-300 animate-pulse" />
                  <div className="absolute inset-4 rounded-full border-2 border-blue-200" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <User size={48} className="text-slate-300 mx-auto mb-2" />
                      <span className="text-xs text-slate-400">人脸识别区域</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 mb-6">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-xs text-blue-600 font-bold">1</span>
                    </div>
                    <span>确保面部清晰可见</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-xs text-blue-600 font-bold">2</span>
                    </div>
                    <span>请勿遮挡面部</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-xs text-blue-600 font-bold">3</span>
                    </div>
                    <span>保持静止</span>
                  </div>
                </div>
                
                <button 
                  onClick={() => handleFaceRecognitionComplete(true)}
                  className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98]"
                >
                  开始识别
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                  <XCircle size={20} className="text-red-500" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-800">驳回结算</h3>
                  <p className="text-xs text-slate-500">请填写驳回原因</p>
                </div>
              </div>
              
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="请输入驳回原因，如：重量有误、价格不对等..."
                className="w-full h-24 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
              />
              
              <div className="flex gap-3 mt-4">
                <button 
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  className="flex-1 py-2.5 rounded-lg border border-slate-200 text-slate-600 font-medium hover:bg-slate-50 transition-colors"
                >
                  取消
                </button>
                <button 
                  onClick={() => {
                    onReject(delivery.id, rejectReason);
                    setShowRejectModal(false);
                    setRejectReason('');
                  }}
                  disabled={!rejectReason.trim()}
                  className={`flex-1 py-2.5 rounded-lg font-bold transition-colors ${
                    rejectReason.trim() 
                      ? 'bg-red-500 text-white hover:bg-red-600' 
                      : 'bg-slate-100 text-slate-400'
                  }`}
                >
                  提交驳回
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const TimelineNode = ({ item, isLast, isCompleted, isCurrent, onPreview }) => {
  const config = DELIVERY_STATUS_CONFIG[item.status] || DELIVERY_STATUS_CONFIG['待送货'];
  const Icon = config.icon;
  
  return (
    <div className="flex items-start gap-3">
      <div className="flex flex-col items-center">
        <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
          isCompleted ? 'bg-green-500' :
          isCurrent ? config.color : 'bg-slate-200'
        }`}>
          {isCompleted ? (
            <CheckCircle size={14} className="text-white" />
          ) : isCurrent ? (
            <Icon size={14} className="text-white" />
          ) : (
            <div className="w-2 h-2 rounded-full bg-slate-400" />
          )}
        </div>
        {!isLast && (
          <div className={`w-0.5 h-8 ${isCompleted ? 'bg-green-400' : 'bg-slate-200'}`} />
        )}
      </div>
      <div className="flex-1 pb-3">
        <div className="flex items-center justify-between">
          <span className="text-xs text-slate-400">{item.time}</span>
          {item.documentData && (
            <button 
              onClick={() => onPreview && onPreview(item)}
              className="text-xs text-blue-500 font-medium flex items-center gap-1 hover:text-blue-600"
            >
              <Eye size={10} />
              查看
            </button>
          )}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
            isCompleted ? 'bg-green-100 text-green-600' :
            isCurrent ? config.lightBg + ' ' + config.textColor :
            'bg-slate-100 text-slate-500'
          }`}>
            {isCompleted && <CheckCircle size={10} />}
            {isCurrent && <Icon size={10} />}
            {item.status}
          </span>
          <span className="text-sm text-slate-700">{item.action}</span>
        </div>
        {item.document && (
          <div 
            onClick={() => item.documentData && onPreview && onPreview(item)}
            className={`flex items-center gap-1 mt-1 text-xs ${item.documentData ? 'text-blue-500 cursor-pointer hover:text-blue-600' : 'text-slate-500'}`}
          >
            <FileText size={10} />
            {item.document}
          </div>
        )}
      </div>
    </div>
  );
};

const DeliveryCard = ({ delivery, index, total, expanded, onToggle, onConfirm, onReject, onPreview, batchMode, selected, onSelect }) => {
  const config = DELIVERY_STATUS_CONFIG[delivery.status] || DELIVERY_STATUS_CONFIG['待送货'];
  const Icon = config.icon;
  const currentTimelineIndex = STATUS_ORDER.indexOf(delivery.status);
  const isInProgress = IN_PROGRESS_STATUSES.includes(delivery.status);
  const needConfirm = delivery.status === NEED_CONFIRM_STATUS;

  return (
    <div className={`bg-white rounded-xl border shadow-sm overflow-hidden mb-3 transition-all ${
      selected ? 'border-blue-500 ring-2 ring-blue-200 bg-blue-50/30' :
      needConfirm ? 'border-orange-300 ring-2 ring-orange-200' :
      isInProgress ? 'border-blue-200 ring-2 ring-blue-100' : 'border-slate-100'
    }`}>
      <div
        className="p-4 cursor-pointer"
        onClick={batchMode && needConfirm ? () => onSelect(delivery.id) : onToggle}
      >
        {/* 批量模式下显示复选框 */}
        {batchMode && needConfirm && (
          <div className="flex items-center gap-3 mb-3 pb-3 border-b border-slate-100">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSelect(delivery.id);
              }}
              className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${
                selected ? 'bg-blue-500 border-blue-500' : 'border-slate-300 hover:border-blue-400'
              }`}
            >
              {selected && <CheckCircle size={14} className="text-white" />}
            </button>
            <span className="text-sm text-slate-600">选择此结算单</span>
            <span className="ml-auto text-sm font-bold text-red-600">¥{delivery.amount?.toLocaleString()}</span>
          </div>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-11 h-11 rounded-xl ${config.color} flex items-center justify-center shadow-sm ${needConfirm ? 'animate-pulse' : ''}`}>
              <Icon size={22} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-slate-800">{delivery.deliveryNo}</span>
              </div>
              <div className="text-xs text-slate-500">批次 {index + 1}/{total}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${config.color} text-white shadow-sm`}>
              {delivery.status}
            </span>
            <div className={`w-6 h-6 rounded-full flex items-center justify-center transition-transform ${
              expanded ? 'rotate-180' : ''
            }`}>
              <ChevronDown size={18} className="text-slate-400" />
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600">
          {delivery.weight && (
            <span className="flex items-center gap-1">
              <Weight size={12} />
              <span className="font-medium">{delivery.weight}吨</span>
            </span>
          )}
          {delivery.amount && (
            <span className="flex items-center gap-1 text-red-600 font-bold">
              ¥{delivery.amount.toLocaleString()}
            </span>
          )}
          {delivery.vehicleNo && (
            <span className="flex items-center gap-1">
              <Truck size={12} />
              {delivery.vehicleNo}
            </span>
          )}
          {delivery.driverName && (
            <span className="flex items-center gap-1">
              <User size={12} />
              {delivery.driverName}
            </span>
          )}
        </div>
        
        {needConfirm && !expanded && (
          <div className="mt-3 pt-3 border-t border-orange-200 bg-gradient-to-r from-orange-50 to-white -mx-4 -mb-4 px-4 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold text-orange-600 flex items-center gap-1">
                <AlertCircle size={12} />
                结算确认
              </div>
              <div className="text-xs text-slate-500">
                {delivery.weight}吨 × ¥{delivery.unitPrice}/吨 = <span className="text-red-600 font-bold">¥{delivery.amount.toLocaleString()}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onReject(delivery.id, '');
                }}
                className="flex-1 py-2 rounded-lg border border-red-200 text-red-600 text-xs font-bold hover:bg-red-50 transition-colors flex items-center justify-center gap-1"
              >
                <XCircle size={14} />
                驳回
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onConfirm(delivery.id);
                }}
                className="flex-1 py-2 rounded-lg bg-green-500 text-white text-xs font-bold hover:bg-green-600 transition-colors flex items-center justify-center gap-1 shadow-sm"
              >
                <CheckCircle size={14} />
                确认结算
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
                className="py-2 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 transition-colors flex items-center gap-1"
              >
                <Eye size={14} />
                查看详情
              </button>
            </div>
          </div>
        )}
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-100 bg-gradient-to-b from-slate-50/80 to-white">
          {needConfirm && (
            <SettlementConfirmSection 
              delivery={delivery}
              onConfirm={onConfirm}
              onReject={onReject}
            />
          )}
          
          <div className={needConfirm ? 'pt-3' : 'pt-4'}>
            <div className="text-xs font-bold text-slate-500 mb-3 flex items-center gap-1">
              <Clock size={12} />
              状态流转
            </div>
            {delivery.timeline && delivery.timeline.map((item, i) => {
              const itemStatusIndex = STATUS_ORDER.indexOf(item.status);
              const isCompleted = itemStatusIndex < currentTimelineIndex;
              const isCurrent = itemStatusIndex === currentTimelineIndex;
              
              return (
                <TimelineNode 
                  key={i} 
                  item={item} 
                  isLast={i === delivery.timeline.length - 1}
                  isCompleted={isCompleted}
                  isCurrent={isCurrent}
                  onPreview={onPreview}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default function OrderDetailPage({ order, onBack, onReorder, highlightDeliveryId, onCreateDelivery, showScheduleModal: externalShowScheduleModal, onCloseScheduleModal, currentUserId, onConfirmOrder, preselectedDeliveryIds }) {
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const [internalShowScheduleModal, setInternalShowScheduleModal] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const deliveryRefs = useRef({});

  // 批量操作相关状态
  const [batchMode, setBatchMode] = useState(false);
  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState(new Set());
  const [showToast] = useState(null);

  // 当有预选中ID时，自动进入批量模式并选中这些送货单
  useEffect(() => {
    if (preselectedDeliveryIds && preselectedDeliveryIds.length > 0) {
      setBatchMode(true);
      setSelectedDeliveryIds(new Set(preselectedDeliveryIds));
    }
  }, [preselectedDeliveryIds]);


  const needConfirmCount = order?.deliveries?.filter(d => d.status === NEED_CONFIRM_STATUS).length || 0;

  const canScheduleDelivery = order && order.status !== '已完成' && order.status !== '待确认';

  const showScheduleModal = externalShowScheduleModal === true || internalShowScheduleModal;

  const isOrganizer = order?.mode === 'other' && order?.organizerId === currentUserId;
  const isSupplier = order?.mode === 'other' && order?.supplierInfo?.id === currentUserId;
  const isPendingConfirm = order?.status === '待确认';

  const handleCloseScheduleModal = () => {
    if (onCloseScheduleModal) {
      onCloseScheduleModal();
    }
    setInternalShowScheduleModal(false);
  };

  const handleOpenScheduleModal = () => {
    setInternalShowScheduleModal(true);
  };

  useEffect(() => {
    if (order?.deliveries) {
      const firstInProgress = order.deliveries.findIndex(d =>
        IN_PROGRESS_STATUSES.includes(d.status) || d.status === NEED_CONFIRM_STATUS
      );
      const initialExpanded = new Set();
      if (firstInProgress >= 0) {
        initialExpanded.add(order.deliveries[firstInProgress].id);
      }
      order.deliveries.forEach(d => {
        if (d.status === NEED_CONFIRM_STATUS) {
          initialExpanded.add(d.id);
        }
      });
      setExpandedIds(initialExpanded);
    }
  }, [order]);

  useEffect(() => {
    if (highlightDeliveryId && order?.deliveries) {
      setExpandedIds(prev => {
        const newSet = new Set(prev);
        newSet.add(highlightDeliveryId);
        return newSet;
      });
      
      setTimeout(() => {
        if (deliveryRefs.current[highlightDeliveryId]) {
          deliveryRefs.current[highlightDeliveryId].scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }
      }, 200);
    }
  }, [highlightDeliveryId, order]);

  const toggleExpand = (deliveryId) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deliveryId)) {
        newSet.delete(deliveryId);
      } else {
        newSet.add(deliveryId);
      }
      return newSet;
    });
  };

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(order?.deliveries?.map(d => d.id) || []));
    }
    setAllExpanded(!allExpanded);
  };

  const handleConfirm = (deliveryId) => {
    console.log('确认结算:', deliveryId);
  };

  const handleReject = (deliveryId, reason) => {
    console.log('驳回结算:', deliveryId, reason);
  };

  // 批量操作相关函数
  const toggleSelectDelivery = (deliveryId) => {
    setSelectedDeliveryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deliveryId)) {
        newSet.delete(deliveryId);
      } else {
        newSet.add(deliveryId);
      }
      return newSet;
    });
  };


  // Toast组件
  const ToastComponent = () => {
    if (!showToast) return null;
    return (
      <div className={`fixed top-20 left-1/2 -translate-x-1/2 z-[100] px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-fade-in ${
        showToast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
      }`}>
        {showToast.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
        <span className="font-medium">{showToast.message}</span>
      </div>
    );
  };

  if (!order) return null;

  return (
    <div className="bg-slate-50 min-h-full pb-24">
      <div className="bg-white sticky top-0 z-10 px-4 py-3 border-b border-slate-200 flex items-center shadow-sm">
        <button onClick={onBack}>
          <ChevronLeft size={24} className="text-slate-700" />
        </button>
        <h2 className="font-bold ml-2 text-slate-800 text-lg">订单详情</h2>
        <div className="ml-auto flex items-center gap-2">
          <span className={`text-xs px-3 py-1.5 rounded-full text-white font-bold ${
            order.status === '待确认' ? 'bg-orange-500' :
            order.status === '进行中' ? 'bg-blue-500' :
            order.status === '已完成' ? 'bg-green-500' : 'bg-slate-500'
          }`}>
            {order.status}
          </span>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-4 text-white shadow-lg">
          <div className="flex items-start justify-between mb-2">
            <div className="font-bold text-lg font-mono">{order.orderNo}</div>
            <div className="flex items-center gap-2">
              {order.demandType === 'closed' && (
                <span className="text-xs px-2 py-1 bg-purple-400/80 rounded-full font-medium">
                  闭口需求
                </span>
              )}
              {order.mode === 'other' && (
                <span className="text-xs px-2 py-1 bg-white/20 rounded-full font-medium">
                  帮别人卖
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Building2 size={14} className="text-blue-200" />
            <span className="text-white font-medium">{order.baseName}</span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <Package size={14} className="text-blue-200" />
            <span className="text-blue-100">{order.material}</span>
            <span className="text-blue-200">·</span>
            <span className="text-blue-100">{order.estimatedWeight}吨</span>
          </div>
          <div className="flex items-center gap-2 text-xs text-blue-200">
            <Calendar size={12} />
            <span>创建于 {order.createdAt}</span>
            {order.deliveryDeadline && (
              <>
                <span className="text-blue-300">·</span>
                <span>截止 {order.deliveryDeadline}</span>
              </>
            )}
          </div>
        </div>

        {isPendingConfirm && isOrganizer && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 px-4 py-3 border-b border-orange-200">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-orange-500" />
                <span className="font-bold text-orange-700">等待实供人确认</span>
              </div>
            </div>
            <div className="p-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <div className="text-xs text-slate-500 mb-2">实供人信息</div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                      <User size={20} className="text-slate-500" />
                    </div>
                    <div>
                      <div className="font-bold text-slate-800">{order.supplierInfo?.name}</div>
                      <div className="text-xs text-slate-500">{order.supplierInfo?.phone}</div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-400 mt-2">
                    可将二维码转发给实供人扫码确认订单
                  </div>
                </div>
                <div className="shrink-0">
                  <div className="w-28 h-28 bg-slate-50 rounded-lg flex items-center justify-center border-2 border-dashed border-slate-200">
                    <div className="text-center">
                      <div className="text-3xl mb-1">📱</div>
                      <p className="text-[10px] text-slate-400">扫码确认</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {needConfirmCount > 0 && (
              <div 
                className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 text-white shadow-lg cursor-pointer hover:shadow-xl transition-shadow active:scale-[0.98]"
                onClick={() => {
                  const firstNeedConfirm = order?.deliveries?.find(d => d.status === NEED_CONFIRM_STATUS);
                  if (firstNeedConfirm) {
                    setExpandedIds(prev => {
                      const newSet = new Set(prev);
                      newSet.add(firstNeedConfirm.id);
                      return newSet;
                    });
                    setTimeout(() => {
                      if (deliveryRefs.current[firstNeedConfirm.id]) {
                        deliveryRefs.current[firstNeedConfirm.id].scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'center' 
                        });
                      }
                    }, 100);
                  }
                }}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                    <AlertCircle size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="font-bold">有{needConfirmCount}个结算单待确认</div>
                    <div className="text-sm text-orange-100">点击查看并处理</div>
                  </div>
                  <ChevronRight size={20} className="text-white/60" />
                </div>
              </div>
            )}

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-xs text-slate-500 mb-1">
                {order.demandType === 'closed' ? '固定重量' : '预估重量'}
              </div>
              <div className="font-bold text-slate-800">
                {order.estimatedWeight}
                <span className="text-xs font-normal text-slate-500 ml-0.5">吨</span>
                {order.demandType === 'closed' && (
                  <span className="text-xs text-purple-500 ml-1">（固定）</span>
                )}
              </div>
            </div>
            <div className="text-center border-x border-slate-100">
              <div className="text-xs text-slate-500 mb-1">实际重量</div>
              <div className="font-bold text-slate-800">
                {order.actualWeight ? <>{order.actualWeight}<span className="text-xs font-normal text-slate-500 ml-0.5">吨</span></> : <span className="text-slate-400 text-sm">待入库</span>}
              </div>
            </div>
            <div className="text-center">
              <div className="text-xs text-slate-500 mb-1">预估金额</div>
              <div className="font-bold text-red-600">¥{order.estimatedAmount?.toLocaleString()}</div>
            </div>
          </div>
          
          {order.demandType === 'closed' && order.deliveryDeadline && (
            <div className="bg-purple-50 rounded-lg p-3 mb-4 border border-purple-100">
              <div className="flex items-center gap-2 text-purple-700">
                <Clock size={14} className="text-purple-500" />
                <span className="text-xs font-medium">闭口需求有效期</span>
                <span className="text-xs font-bold">截止 {order.deliveryDeadline}</span>
              </div>
              <div className="text-xs text-purple-600 mt-1">
                请在截止日期前完成送货，送货量固定为 {order.estimatedWeight} 吨
              </div>
            </div>
          )}
          
          {order.deliveries && order.deliveries.length > 0 && (
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1.5">
                <span>送货进度</span>
                <span className="font-medium">{order.deliveries?.length || 0}批</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden flex">
                {[
                  { key: 'waiting', color: 'bg-slate-400', count: order.progress?.waiting || 0 },
                  { key: 'arrived', color: 'bg-blue-400', count: order.progress?.arrived || 0 },
                  { key: 'stored', color: 'bg-purple-400', count: order.progress?.stored || 0 },
                  { key: 'needConfirm', color: 'bg-orange-400', count: order.progress?.needConfirm || 0 },
                  { key: 'settled', color: 'bg-amber-400', count: order.progress?.settled || 0 },
                  { key: 'invoiced', color: 'bg-teal-400', count: order.progress?.invoiced || 0 },
                  { key: 'paid', color: 'bg-green-400', count: order.progress?.paid || 0 },
                ].map(seg => (
                  <div 
                    key={seg.key}
                    className={`${seg.color} transition-all`}
                    style={{ width: `${(seg.count / (order.deliveries?.length || 1)) * 100}%` }}
                  />
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-2 text-[10px] text-slate-500">
                {order.progress?.waiting > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-400" />待送货{order.progress.waiting}</span>}
                {order.progress?.arrived > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-400" />已到场{order.progress.arrived}</span>}
                {order.progress?.stored > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400" />已入库{order.progress.stored}</span>}
                {order.progress?.needConfirm > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400" />待结算{order.progress.needConfirm}</span>}
                {order.progress?.settled > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" />已结算{order.progress.settled}</span>}
                {order.progress?.invoiced > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-400" />已开票{order.progress.invoiced}</span>}
                {order.progress?.paid > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400" />已付款{order.progress.paid}</span>}
              </div>
            </div>
          )}
        </div>

        {order.supplierInfo && !(isPendingConfirm && isOrganizer) && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <div className="flex items-center gap-2 mb-2">
              <User size={14} className="text-green-500" />
              <span className="text-xs text-slate-500">实供人</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="font-bold text-slate-800">{order.supplierInfo.name}</span>
                <span className="text-xs text-slate-500 ml-2">{order.supplierInfo.phone}</span>
              </div>
              <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">已实名</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Truck size={16} className="text-blue-500" />
              送货单列表
            </h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">共 {order.deliveries?.length || 0} 批</span>
              {order.deliveries && order.deliveries.length > 0 && (
                <button
                  onClick={toggleAll}
                  className="text-xs text-blue-500 font-medium flex items-center gap-1 hover:text-blue-600"
                >
                  {allExpanded ? (
                    <>
                      <ChevronUp size={14} />
                      收起
                    </>
                  ) : (
                    <>
                      <ChevronDown size={14} />
                      展开
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
          
          {order.deliveries && order.deliveries.length > 0 ? (
            order.deliveries.map((delivery, index) => (
              <div
                key={delivery.id}
                ref={el => deliveryRefs.current[delivery.id] = el}
              >
                <DeliveryCard
                  delivery={delivery}
                  index={index}
                  total={order.deliveries.length}
                  expanded={expandedIds.has(delivery.id)}
                  onToggle={() => toggleExpand(delivery.id)}
                  onConfirm={handleConfirm}
                  onReject={handleReject}
                  onPreview={setPreviewDocument}
                  batchMode={batchMode}
                  selected={selectedDeliveryIds.has(delivery.id)}
                  onSelect={toggleSelectDelivery}
                />
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <Truck size={32} className="mx-auto mb-2 text-slate-300" />
              <p className="text-slate-500">暂无送货单</p>
              <p className="text-xs text-slate-400 mt-1">点击上方"预约发货"按钮创建</p>
            </div>
          )}
        </div>

        {!(isPendingConfirm && isOrganizer) && (
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <FileText className="text-blue-500" size={20} />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-800">电子采购合同.pdf</div>
                  <div className="text-xs text-slate-400">已签署 · {order.createdAt}</div>
                </div>
              </div>
              <div className="flex gap-2">
                <button className="flex items-center gap-1 text-blue-500 text-xs border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">
                  <Eye size={14} />
                  查看
                </button>
                <button className="flex items-center gap-1 text-blue-500 text-xs border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-50 transition-colors">
                  <Download size={14} />
                  下载
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-16 left-0 right-0 z-20 flex justify-center pointer-events-none">
        <div className="w-full max-w-md bg-white border-t border-slate-200 p-4 pointer-events-auto">
          {isPendingConfirm && isOrganizer ? (
            <div className="flex items-center justify-center gap-2 py-3 bg-orange-50 rounded-xl text-orange-600">
              <Clock size={18} />
              <span className="font-medium">等待实供人确认订单</span>
            </div>
          ) : isPendingConfirm && isSupplier ? (
            <button
              onClick={() => onConfirmOrder && onConfirmOrder(order)}
              className="w-full py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <CheckCircle size={20} />
              确认订单
            </button>
          ) : canScheduleDelivery ? (
            <button
              onClick={handleOpenScheduleModal}
              className="w-full py-3.5 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold text-base shadow-lg hover:shadow-xl transition-all active:scale-[0.98] flex items-center justify-center gap-2"
            >
              <Truck size={20} />
              预约发货
            </button>
          ) : null}
        </div>
      </div>
      
      <ScheduleDeliveryModal 
        visible={showScheduleModal}
        onClose={handleCloseScheduleModal}
        onSubmit={(data) => {
          if (onCreateDelivery) {
            onCreateDelivery(order.id, data);
          }
          handleCloseScheduleModal();
        }}
        order={order}
      />
      
      <DocumentPreviewModal
        visible={!!previewDocument}
        onClose={() => setPreviewDocument(null)}
        document={previewDocument}
      />

      {/* Toast提示 */}
      <ToastComponent />
    </div>
  );
}
